/**
 * Compatibility shim for react-router v5 → v6 migration.
 *
 * Provides `withRouter` HOC and `RouteComponentProps` type that mimics the v5 API
 * using v6 hooks under the hood. This allows incremental migration of components
 * from withRouter to direct hook usage.
 *
 * Usage:
 *   import { withRouter, RouteComponentProps } from '../../Utils/withRouter';
 *
 * Can be removed once all components are migrated to use hooks directly.
 */
import React, { useMemo } from 'react';
import { type Location, type Params, useLocation, useNavigate, useParams } from 'react-router-dom';
import type { NavigateFunction } from 'react-router';

/**
 * v5-compatible history object built on top of v6's useNavigate.
 */
export interface CompatHistory {
  push(path: string): void;
  push(path: string, state: any): void;
  push(location: { pathname: string; search?: string; hash?: string; state?: any }): void;
  replace(path: string): void;
  replace(path: string, state: any): void;
  replace(location: { pathname: string; search?: string; hash?: string; state?: any }): void;
  goBack(): void;
  goForward(): void;
  go(n: number): void;
  location: Location;
  listen: never; // Mark as unavailable to catch direct usage at compile time
  block: never;
}

/**
 * v5-compatible match object.
 */
export interface CompatMatch<P = Params<string>> {
  params: P;
  url: string;
  path: string;
  isExact: boolean;
}

/**
 * v5-compatible RouteComponentProps type.
 */
export interface RouteComponentProps<P = {}> {
  history: CompatHistory;
  location: Location;
  match: CompatMatch<P>;
}

function buildHistoryFromNavigate(navigate: NavigateFunction, location: Location): CompatHistory {
  return {
    push(to: any, state?: any) {
      if (typeof to === 'string') {
        navigate(to, state !== undefined ? { state } : undefined);
      } else {
        const path = to.pathname + (to.search || '') + (to.hash || '');
        navigate(path, to.state !== undefined ? { state: to.state } : undefined);
      }
    },
    replace(to: any, state?: any) {
      if (typeof to === 'string') {
        navigate(to, { replace: true, ...(state !== undefined ? { state } : {}) });
      } else {
        const path = to.pathname + (to.search || '') + (to.hash || '');
        navigate(path, { replace: true, ...(to.state !== undefined ? { state: to.state } : {}) });
      }
    },
    goBack() {
      navigate(-1);
    },
    goForward() {
      navigate(1);
    },
    go(n: number) {
      navigate(n);
    },
    location,
  } as CompatHistory;
}

/**
 * withRouter HOC — wraps a component and injects history, location, match props
 * using react-router v6 hooks.
 */
export function withRouter<P extends RouteComponentProps<any>>(Component: React.ComponentType<P>) {
  const displayName = `withRouter(${Component.displayName || Component.name || 'Component'})`;

  const WrappedComponent = React.forwardRef<any, Omit<P, keyof RouteComponentProps>>(
    (props, ref) => {
      const navigate = useNavigate();
      const location = useLocation();
      const params = useParams();

      // Memoize history and match to prevent new object references on every render,
      // which would cause infinite re-render loops in wrapped components.
      const history = useMemo(
        () => buildHistoryFromNavigate(navigate, location),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [navigate, location.pathname, location.search, location.hash, location.state],
      );

      const match: CompatMatch = useMemo(
        () => ({
          params,
          url: location.pathname,
          path: location.pathname,
          isExact: true,
        }),

        [params, location.pathname],
      );

      return (
        <Component
          {...(props as any)}
          ref={ref}
          history={history}
          location={location}
          match={match}
        />
      );
    },
  );

  WrappedComponent.displayName = displayName;
  return WrappedComponent;
}
